import { Copy, Check, Download } from 'lucide-react';
import { useCallback } from 'react';
import useCopyState from './useCopyState';

interface ArtifactDataTableProps {
  title: string;
  data: {
    columns: Array<{ key: string; label: string }>;
    rows: Array<Record<string, string | number>>;
  };
}

function ArtifactDataTable({ title, data }: ArtifactDataTableProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    const header = data.columns.map((c) => c.label).join('\t');
    const rows = data.rows
      .map((row) => data.columns.map((c) => row[c.key] ?? '').join('\t'))
      .join('\n');
    navigator.clipboard.writeText(`${header}\n${rows}`);
    triggerCopied();
  }, [data, triggerCopied]);

  const handleDownloadCSV = useCallback(() => {
    const header = data.columns.map((c) => `"${c.label}"`).join(',');
    const rows = data.rows
      .map((row) =>
        data.columns
          .map((c) => {
            const val = row[c.key] ?? '';
            return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
          })
          .join(','),
      )
      .join('\n');
    const csv = `${header}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, title]);

  if (!data?.columns || !data?.rows) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Copy as tab-separated (for Excel)"
            aria-label="Copy as tab-separated values"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={handleDownloadCSV}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Download CSV"
            aria-label="Download CSV"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-neutral-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50">
              {data.columns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wide border-b border-neutral-200"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIdx) => (
              <tr
                // eslint-disable-next-line react/no-array-index-key
                key={rowIdx}
                className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50"
              >
                {data.columns.map((col) => (
                  <td key={col.key} className="px-3 py-2 text-neutral-700">
                    {row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ArtifactDataTable;
